<template>
  <div class="col-12" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
    <div class="card mb-3" style="margin-top: 2.5rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100 d-flex justify-content-between align-items-center">
          <h6 class="text-white text-capitalize ps-3 pe-3 mb-0" style="font-size: 1rem">
            {{ $t('salary_management.calculation_detail_title') }}
          </h6>
        </div>
      </div>

      <div class="card-body" v-if="calc">
        <div class="row mb-4">
          <div class="col-md-4">
            <p class="text-xs text-muted mb-0">{{ $t('salary_management.employee') }}</p>
            <p class="fw-bold">{{ calc.model_name }}</p>
          </div>
          <div class="col-md-4">
            <p class="text-xs text-muted mb-0">{{ $t('salary_management.job_title') }}</p>
            <p class="fw-bold">{{ calc.job_title || '—' }}</p>
          </div>
          <div class="col-md-4">
            <p class="text-xs text-muted mb-0">{{ $t('salary_management.month') }}</p>
            <p class="fw-bold">{{ calc.month_label }}</p>
          </div>
        </div>

        <div class="row g-3 mb-4">
          <div class="col-6 col-md-3">
            <div class="p-3 rounded text-center" :class="isBasicSalaryUnused ? 'bg-secondary-subtle' : 'bg-light'">
              <p class="text-xs text-muted mb-1">{{ $t('salary_management.basic_salary') }}</p>
              <p class="fw-bold mb-0" :class="{ 'text-muted': isBasicSalaryUnused }">
                {{ calc.basic_salary }} {{ $t('common.currency') }}
              </p>
              <p v-if="isBasicSalaryUnused" class="text-xxs text-muted mb-0 mt-1">
                {{ $t('salary_management.basic_salary_not_counted') }}
              </p>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="p-3 bg-light rounded text-center">
              <p class="text-xs text-muted mb-1">{{ $t('salary_management.lessons_amount') }}</p>
              <p class="fw-bold mb-0">{{ calc.lessons_amount }} {{ $t('common.currency') }}</p>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="p-3 bg-success-subtle rounded text-center">
              <p class="text-xs text-muted mb-1">{{ $t('salary_management.rewards') }}</p>
              <p class="fw-bold mb-0 text-success">{{ calc.rewards }} {{ $t('common.currency') }}</p>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="p-3 bg-danger-subtle rounded text-center">
              <p class="text-xs text-muted mb-1">{{ $t('salary_management.deductions') }}</p>
              <p class="fw-bold mb-0 text-danger">{{ calc.deductions }} {{ $t('common.currency') }}</p>
            </div>
          </div>
        </div>

        <div class="row g-3 mb-4" v-if="calc.model_kind === 'teacher'">
          <div class="col-6 col-md-3">
            <p class="text-xs text-muted mb-1">{{ $t('salary_management.salary_type') }}</p>
            <p class="fw-bold">{{ salaryTypeLabel(calc.salary_type) }}</p>
          </div>
          <div class="col-6 col-md-3">
            <p class="text-xs text-muted mb-1">{{ $t('salary_management.attended_lessons') }}</p>
            <p class="fw-bold">{{ calc.attended_lessons }} / {{ calc.total_lessons }}</p>
          </div>
          <div class="col-6 col-md-3">
            <p class="text-xs text-muted mb-1">{{ $t('salary_management.working_days') }}</p>
            <p class="fw-bold">{{ calc.working_days ?? '—' }} / {{ calc.month_days ?? '—' }}</p>
          </div>
          <div class="col-6 col-md-3">
            <p class="text-xs text-muted mb-1">{{ $t('salary_management.absent_days') }}</p>
            <p class="fw-bold">{{ calc.absent_days ?? '—' }}</p>
          </div>
        </div>

        <div class="alert alert-primary d-flex justify-content-between align-items-center">
          <span>{{ $t('salary_management.net_salary') }}</span>
          <span class="fw-bold fs-5">{{ calc.net_salary }} {{ $t('common.currency') }}</span>
        </div>

        <h6 class="mt-4 mb-3">{{ $t('salary_management.statements') }}</h6>
        <div class="table-responsive">
          <table class="table align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('salary_management.date') }}</th>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('salary_management.description') }}</th>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('salary_management.credit') }}</th>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('salary_management.debit') }}</th>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('salary_management.balance') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="statements.length === 0">
                <td colspan="5" class="text-center py-3 text-muted">{{ $t('salary_management.no_statements') }}</td>
              </tr>
              <tr v-for="s in statements" :key="s.id">
                <td><span class="text-sm">{{ s.date }}</span></td>
                <td><span class="text-sm">{{ s.description }}</span></td>
                <td class="text-center"><span class="text-sm text-success">{{ s.credit || 0 }}</span></td>
                <td class="text-center"><span class="text-sm text-danger">{{ s.debit || 0 }}</span></td>
                <td class="text-center"><span class="text-sm">{{ s.balance }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <LoadingSpinner v-else :message="$t('common.loading')" class="py-5" />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const toast = useToast();

const calc = computed(() => store.getters['salaryManagement/currentCalculation']);
const statements = computed(() => store.getters['salaryManagement/currentCalculationStatements']);

// For a "per_lesson" teacher, basic_salary is just their configured base
// rate for reference — it was never actually part of net_salary that
// month (only lessons_amount was), so make that explicit instead of
// letting it look like part of their pay.
const isBasicSalaryUnused = computed(() => calc.value?.salary_type === 'per_lesson');

const salaryTypeLabel = (type) => {
  const map = {
    fixed: t('teachers.salaryTypeFixed'),
    per_lesson: t('teachers.salaryTypePerLesson'),
    fixed_plus_lessons: t('teachers.salaryTypeFixedPlusLessons'),
  };
  return type ? (map[type] || type) : '—';
};

onMounted(async () => {
  try {
    await store.dispatch('salaryManagement/fetchCalculationDetail', route.params.id);
  } catch (e) {
    toast.error(t('common.error'));
  }
});
</script>
